import { useCallback, useEffect, useMemo, useState } from 'react';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';
import {
  ALL_PORTFOLIOS_OPTION,
  getInvestPortfolio,
  writeStoredInvestPortfolio,
} from '#app/helpers/Cache/InvestPortfolioHelper';

export type InvestPortfolioOption = {
  id: number | null;
  name: string;
};

type PortfolioTab = {
  id?: number | null;
  title?: string;
};

type UseInvestPortfolioOptions = {
  storageKey?: string;
  clientId?: number | null;
  userTabs?: PortfolioTab[];
  allowAll?: boolean;
};

/**
 * Выбор портфеля на инвестициях — по той же схеме, что useInvestCurrencyAndSearch.
 */
export const useInvestPortfolio = (options: UseInvestPortfolioOptions = {}) => {
  const {
    storageKey = LocalStorageHelper.INVEST_PORTFOLIO,
    clientId,
    userTabs = [],
    allowAll: allowAllOption = false,
  } = options;

  const allowAll = allowAllOption && userTabs.length > 1;

  const portfolioContext = useMemo(
    () => ({ allowAll, clientId: clientId ?? undefined, userTabs }),
    [allowAll, clientId, userTabs]
  );

  const [portfolio, setPortfolio] = useState<InvestPortfolioOption>(() =>
    getInvestPortfolio(storageKey, portfolioContext)
  );

  useEffect(() => {
    setPortfolio(getInvestPortfolio(storageKey, portfolioContext));
  }, [storageKey, portfolioContext]);

  const handleSelectPortfolio = useCallback(
    (item: InvestPortfolioOption | number | null | undefined) => {
      if (item == null) {
        return;
      }

      let resolved: InvestPortfolioOption | null = null;

      if (typeof item === 'number') {
        const tab = userTabs.find((t) => Number(t.id) === item);
        if (tab) {
          resolved = { id: item, name: tab.title ?? '' };
        }
      } else if (typeof item === 'object' && item.name) {
        const rawId = (item as { id?: string | number | null }).id;
        const parsedId = rawId === 'all' || rawId == null ? null : Number(rawId);
        const id = parsedId != null && Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
        resolved = {
          id,
          name: item.name,
        };
      }

      if (!resolved) {
        return;
      }

      writeStoredInvestPortfolio(resolved, storageKey);
      setPortfolio(resolved);
    },
    [storageKey, userTabs]
  );

  const setCollectiveUserId = useCallback(
    (portfolioId: number | null | undefined) => {
      if (portfolioId == null) {
        handleSelectPortfolio(allowAll ? ALL_PORTFOLIOS_OPTION : null);
        return;
      }
      handleSelectPortfolio(portfolioId);
    },
    [allowAll, handleSelectPortfolio]
  );

  return {
    portfolio,
    collectiveUserId: portfolio.id ?? null,
    handleSelectPortfolio,
    setCollectiveUserId,
  };
};
